import React from 'react';
import { ArrowRight, FileText, Video, Award, ExternalLink, Sparkles, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { usePortfolio } from '../context/PortfolioContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { hero, media, talks, loading } = usePortfolio();
  const [ref1, isVisible1] = useScrollAnimation(0.1);
  const [ref2, isVisible2] = useScrollAnimation(0.1);

  // Get featured content
  const featuredMedia = (media || []).filter(item => item.featured).slice(0, 3);
  const featuredTalks = (talks || []).filter(talk => talk.featured).slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, x: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO 
        title="Mir Shahrukh Islam | Tech Entrepreneur & Forbes 30 Under 30"
        description="Building technology companies that solve real-world problems. Co-Founder & CEO of Bondstein Technologies, Forbes 30 Under 30 Asia."
        url="/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 pt-20 flex items-center relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-100/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-amber-100/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              className="space-y-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="space-y-4">
                <motion.div variants={itemVariants}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full text-sm font-medium">
                    <Sparkles size={16} className="text-amber-400" />
                    Curious, Hungry & Brave
                  </span>
                </motion.div>
                
                <motion.h1 
                  variants={itemVariants}
                  className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1]"
                >
                  Mir Shahrukh Islam
                </motion.h1>
                
                <motion.p 
                  variants={itemVariants}
                  className="text-xl md:text-2xl text-gray-600 font-medium"
                >
                  Tech Entrepreneur • Innovation Leader
                </motion.p>
              </div>

              <motion.p 
                variants={itemVariants}
                className="text-lg text-gray-700 leading-relaxed max-w-xl"
              >
                Building technology companies that solve real-world problems. Co-Founder & CEO of Bondstein Technologies, Forbes 30 Under 30 Asia, and passionate about sharing insights on entrepreneurship, IoT, and digital transformation.
              </motion.p>

              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap gap-4"
              >
                <Button
                  onClick={() => navigate('/about')}
                  size="lg"
                  className="bg-gray-900 text-white hover:bg-gray-800 group shadow-lg shadow-gray-900/20 hover:shadow-xl hover:shadow-gray-900/30 transition-all duration-300"
                  data-testid="hero-learn-more-btn"
                >
                  Learn More About Me
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
                <Button
                  onClick={() => navigate('/insights')}
                  size="lg"
                  variant="outline"
                  className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300"
                  data-testid="hero-insights-btn"
                >
                  Read My Insights
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div 
              className="relative"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-6 -right-6 w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl" />
                <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-2xl relative z-10">
                  <img
                    src={hero.image}
                    alt={hero.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Stats badge */}
                <motion.div 
                  className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 z-20"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Award className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Bondstein Technologies</p>
                      <p className="font-semibold text-gray-900">Co-Founder & CEO</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Articles Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Featured Articles
              </h2>
              <p className="text-lg text-gray-700">
                Latest insights on entrepreneurship, technology, and building ventures.
              </p>
            </div>
            <Button
              onClick={() => navigate('/insights')}
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white hidden md:flex group"
              data-testid="view-all-articles-btn"
            >
              View All Articles
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Button>
          </div>

          <div ref={ref1} className="grid md:grid-cols-3 gap-8">
            {featuredMedia.map((article, index) => (
              <div
                key={article.id}
                className={isVisible1 ? 'animate-fade-in-up' : ''}
                style={{ 
                  animationDelay: `${index * 150}ms`,
                  opacity: isVisible1 ? undefined : 1 
                }}
              >
                <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2">
                  <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <Badge className="absolute top-4 left-4 bg-gray-900 text-white">
                      Featured
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText size={14} />
                        {article.publication}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                        {article.description}
                      </p>
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
                      >
                        Read Article
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center md:hidden">
            <Button
              onClick={() => navigate('/insights')}
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              View All Articles
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Talks Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Speaking Engagements
              </h2>
              <p className="text-lg text-gray-700">
                Talks and panels on technology, entrepreneurship, and innovation.
              </p>
            </div>
            <Button
              onClick={() => navigate('/insights')}
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white hidden md:flex group"
              data-testid="view-all-talks-btn"
            >
              View All Talks
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Button>
          </div>

          <div ref={ref2} className="grid md:grid-cols-3 gap-8">
            {featuredTalks.map((talk, index) => (
              <div
                key={talk.id}
                className={isVisible2 ? 'animate-fade-in-up' : ''}
                style={{ 
                  animationDelay: `${index * 150}ms`,
                  opacity: isVisible2 ? undefined : 1 
                }}
              >
                <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2">
                  <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={talk.thumbnail}
                      alt={talk.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Video className="text-white" size={48} />
                    </div>
                    <Badge className="absolute top-4 left-4 bg-gray-900 text-white">
                      Featured
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">
                        {talk.title}
                      </h3>
                      <p className="text-sm text-gray-700 font-medium">{talk.event}</p>
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                        {talk.description}
                      </p>
                      {talk.videoUrl && (
                        <a
                          href={talk.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
                        >
                          Watch Talk
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center md:hidden">
            <Button
              onClick={() => navigate('/insights')}
              variant="outline"
              className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              View All Talks
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* The Bondstein Story Video Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Bondstein Story
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              A deep dive into building a frontier tech company in emerging markets.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <a 
              href="https://www.youtube.com/watch?v=tpSvI81cHs0"
              target="_blank"
              rel="noopener noreferrer"
              className="block group w-full max-w-4xl"
              data-testid="bondstein-story-video"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://img.youtube.com/vi/tpSvI81cHs0/maxresdefault.jpg"
                  alt="The Bondstein Story"
                  className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-24 h-24 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Play className="text-gray-900 ml-1" size={40} />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white font-semibold text-xl">The Bondstein Story</p>
                  <p className="text-gray-300">Watch the full conversation</p>
                </div>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Let&apos;s Connect
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Interested in collaboration, speaking opportunities, or just want to connect? I&apos;d love to hear from you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="cta-contact-btn"
              >
                Get in Touch
              </Button>
              <Button
                onClick={() => navigate('/insights')}
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                Explore Insights
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
